<template>
  <div class="i-multiselect" ref="container">
    <!-- Trigger / Display -->
    <div
      class="ms-trigger"
      :class="{
        'ms-open': isOpen,
        'ms-disabled': disabled,
        'ms-error': error
      }"
      @click="toggle"
    >
      <div class="ms-value-area">
        <template v-if="selectedItems.length > 0">
          <span
            v-for="item in visibleChips"
            :key="item.value"
            class="ms-chip"
          >
            <span class="ms-chip-text">{{ item.label }}</span>
            <span class="ms-chip-remove" @click.stop="removeItem(item.value)">&times;</span>
          </span>
          <span v-if="hiddenCount > 0" class="ms-more">
            +{{ hiddenCount }}
          </span>
        </template>
        <span v-else class="ms-placeholder">{{ placeholder }}</span>
      </div>
      <div class="ms-arrow">
        <svg
          class="ms-arrow-icon"
          :class="{ 'ms-arrow-open': isOpen }"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd"/>
        </svg>
      </div>
    </div>

    <!-- Dropdown -->
    <div v-show="isOpen" class="ms-dropdown">
      <div v-if="filter" class="ms-search-box">
        <input
          ref="searchInput"
          v-model="search"
          type="text"
          class="ms-search-input"
          placeholder="Buscar..."
          @click.stop
          @input="onSearchInput"
        />
        <svg v-if="!search" class="ms-search-icon" viewBox="0 0 20 20" fill="currentColor">
          <path fill-rule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clip-rule="evenodd"/>
        </svg>
        <span v-else class="ms-search-clear" @click.stop="search = ''">&times;</span>
      </div>

      <div class="ms-options-list">
        <div
          v-for="option in filteredOptions"
          :key="option.value"
          class="ms-option"
          :class="{ 'ms-option-selected': isSelected(option.value) }"
          @click.stop="toggleOption(option.value)"
        >
          <div class="ms-checkbox" :class="{ 'ms-checkbox-checked': isSelected(option.value) }">
            <svg v-if="isSelected(option.value)" viewBox="0 0 20 20" fill="currentColor" class="ms-check-icon">
              <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/>
            </svg>
          </div>
          <span class="ms-option-text">{{ option.label }}</span>
        </div>

        <div v-if="filteredOptions.length === 0" class="ms-empty">
          Nenhum resultado encontrado
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'IMultiselect',
  props: {
    value: {
      type: Array,
      default: () => []
    },
    options: {
      type: Array,
      default: () => []
    },
    placeholder: {
      type: String,
      default: 'Selecione'
    },
    disabled: {
      type: Boolean,
      default: false
    },
    filter: {
      type: Boolean,
      default: true
    },
    maxSelectedLabels: {
      type: Number,
      default: 3
    },
    error: {
      type: Boolean,
      default: false
    },
    // Modo remoto: a busca é feita no servidor (emite @search debounced).
    // O filtro client-side é desligado e os labels dos selecionados são cacheados.
    remote: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      isOpen: false,
      search: '',
      searchTimer: null,
      selectedCache: {}
    }
  },
  computed: {
    selectedValues () {
      return Array.isArray(this.value) ? this.value : []
    },
    selectedItems () {
      return this.selectedValues
        .map(val => this.options.find(o => o.value === val) || this.selectedCache[val] || null)
        .filter(Boolean)
    },
    visibleChips () {
      return this.selectedItems.slice(0, this.maxSelectedLabels)
    },
    hiddenCount () {
      return Math.max(0, this.selectedItems.length - this.maxSelectedLabels)
    },
    filteredOptions () {
      // Modo remoto: o servidor já filtrou — não filtra de novo no client.
      if (this.remote) return this.options
      if (!this.search) return this.options
      const q = this.search.toLowerCase()
      return this.options.filter(o => o.label.toLowerCase().includes(q))
    }
  },
  watch: {
    // Cacheia o label dos itens selecionados sempre que as opções mudarem,
    // pra os chips continuarem aparecendo mesmo após uma nova busca remota.
    options: {
      handler () { this.cacheSelected() },
      immediate: true
    },
    value () { this.cacheSelected() }
  },
  mounted () {
    document.addEventListener('click', this.onClickOutside)
  },
  beforeDestroy () {
    document.removeEventListener('click', this.onClickOutside)
  },
  methods: {
    isSelected (value) {
      return this.selectedValues.includes(value)
    },
    toggleOption (value) {
      const current = [...this.selectedValues]
      const idx = current.indexOf(value)
      if (idx >= 0) {
        current.splice(idx, 1)
      } else {
        current.push(value)
      }
      this.$emit('input', current)
    },
    removeItem (value) {
      const current = this.selectedValues.filter(v => v !== value)
      this.$emit('input', current)
    },
    toggle () {
      if (this.disabled) return
      this.isOpen = !this.isOpen
      if (this.isOpen) {
        this.$nextTick(() => {
          if (this.$refs.searchInput) this.$refs.searchInput.focus()
        })
      }
    },
    onClickOutside (e) {
      if (this.$refs.container && !this.$refs.container.contains(e.target)) {
        this.isOpen = false
        this.search = ''
      }
    },
    cacheSelected () {
      this.selectedValues.forEach(val => {
        const opt = this.options.find(o => o.value === val)
        if (opt) this.$set(this.selectedCache, val, opt)
      })
    },
    onSearchInput () {
      if (!this.remote) return
      clearTimeout(this.searchTimer)
      this.searchTimer = setTimeout(() => {
        this.$emit('search', this.search)
      }, 350)
    }
  }
}
</script>

<style scoped>
.i-multiselect {
  position: relative;
  width: 100%;
}

.ms-trigger {
  display: flex;
  align-items: center;
  min-height: 38px;
  max-height: 38px;
  background: #fff;
  border: 1px solid #ced4da;
  border-radius: 0.25rem;
  cursor: pointer;
  transition: border-color 0.15s;
}

.ms-trigger:hover {
  border-color: #a0a4a8;
}

.ms-trigger.ms-open {
  border-color: #a0a4a8;
  box-shadow: 0 0 0 1px #e5e7eb;
}

.ms-trigger.ms-disabled {
  background: #e9ecef;
  cursor: not-allowed;
  opacity: 0.7;
}

.ms-trigger.ms-error {
  border-color: #f46a6a !important;
}

.ms-value-area {
  flex: 1;
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  gap: 0.25rem;
  padding: 0 0.75rem;
  min-height: 36px;
  overflow: hidden;
}

.ms-placeholder {
  color: #adb5bd;
  font-size: 0.8125rem;
}

.ms-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.2rem;
  background: #556ee6;
  color: #fff;
  border-radius: 1rem;
  padding: 0.1rem 0.45rem;
  font-size: 0.75rem;
  max-width: 140px;
  white-space: nowrap;
}

.ms-chip-text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ms-chip-remove {
  cursor: pointer;
  font-size: 0.9rem;
  line-height: 1;
  opacity: 0.8;
  margin-left: 2px;
}

.ms-chip-remove:hover {
  opacity: 1;
}

.ms-more {
  font-size: 0.75rem;
  color: #6b7280;
  padding: 0.1rem 0.35rem;
  background: #f3f4f6;
  border-radius: 1rem;
  white-space: nowrap;
}

.ms-arrow {
  display: flex;
  align-items: center;
  padding: 0 0.5rem;
  color: #6b7280;
}

.ms-arrow-icon {
  width: 16px;
  height: 16px;
  transition: transform 0.2s;
}

.ms-arrow-open {
  transform: rotate(180deg);
}

.ms-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 1050;
  background: #fff;
  border: 1px solid #ced4da;
  border-radius: 0.25rem;
  box-shadow: 0 4px 6px -1px rgba(0,0,0,.1), 0 2px 4px -2px rgba(0,0,0,.1);
  margin-top: 2px;
}

.ms-search-box {
  padding: 0.4rem;
  border-bottom: 1px solid #e9ecef;
  position: relative;
}

.ms-search-input {
  width: 100%;
  padding: 0.35rem 2rem 0.35rem 0.6rem;
  border: 1px solid #ced4da;
  border-radius: 0.25rem;
  font-size: 0.8125rem;
  outline: none;
  background: #fff;
  color: #495057;
  box-sizing: border-box;
}

.ms-search-input:focus {
  border-color: #a0a4a8;
}

.ms-search-icon {
  position: absolute;
  right: 0.85rem;
  top: 50%;
  transform: translateY(-50%);
  width: 14px;
  height: 14px;
  color: #adb5bd;
  pointer-events: none;
}

.ms-search-clear {
  position: absolute;
  right: 0.85rem;
  top: 50%;
  transform: translateY(-50%);
  cursor: pointer;
  color: #adb5bd;
  font-size: 1rem;
  line-height: 1;
}

.ms-search-clear:hover {
  color: #6b7280;
}

.ms-options-list {
  max-height: 200px;
  overflow-y: auto;
  padding: 0.25rem 0;
}

.ms-option {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.45rem 0.65rem;
  cursor: pointer;
  font-size: 0.8125rem;
  color: #495057;
  transition: background 0.1s;
}

.ms-option:hover {
  background: #f8f9fa;
}

.ms-option-selected {
  background: #eef2ff;
}

.ms-option-selected:hover {
  background: #e0e7ff;
}

.ms-checkbox {
  width: 16px;
  height: 16px;
  min-width: 16px;
  border: 2px solid #ced4da;
  border-radius: 0.2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #fff;
  transition: all 0.15s;
}

.ms-checkbox-checked {
  background: #556ee6;
  border-color: #556ee6;
}

.ms-check-icon {
  width: 11px;
  height: 11px;
  color: #fff;
}

.ms-empty {
  padding: 0.6rem;
  color: #adb5bd;
  text-align: center;
  font-size: 0.8125rem;
}
</style>
